/**
 * Bird-footprint trail reveals — staggered opacity pops (scroll-timed, play-once).
 * Global stagger/duration apply to every instance; per-instance presets own
 * start delay + viewport threshold. See docs/04-motion.md (Motion viewport reveals).
 */

/** Shared timing for every footprint trail on the page. */
export const birdFootprints = {
  /**
   * Wall-clock delay between successive foot pops (seconds).
   * Tune here — all instances share this gap.
   */
  staggerSeconds: 0.22,
  /**
   * Per-foot opacity transition duration. Locked at 0 — instant 0→1 pop,
   * stagger alone carries the sequence.
   */
  popDurationSeconds: 0,
  /** Default fraction of the trail that must intersect the viewport. */
  amount: 0.2,
} as const;

/**
 * Intro/2 (“Flyways span entire oceans…”) — footprints wait until the prose
 * block’s fade reaches `readyMinOpacity`, then wait `afterProseDelaySeconds`
 * before the left trail; right trail follows after the last left foot plus
 * `betweenTrailsDelaySeconds`.
 */
export const introPart2Footprints = {
  /**
   * Wall-clock delay after intro part2 prose is ready before the first left
   * foot pops.
   */
  afterProseDelaySeconds: 0.2,
  /**
   * Prose opacity that unblocks footprint trails. Default discernibility is
   * 0.95 (~0.93s into the 1.2s power2.out fade); 0.2 fires ~0.8s earlier so
   * feet start during the late prose fade rather than after it settles.
   */
  readyMinOpacity: 0.2,
  /**
   * Pause after the last left foot pops before the first right foot pops.
   */
  betweenTrailsDelaySeconds: 0.45,
  amount: 0.25,
} as const;

/**
 * State / Red List Categories — trail waits after scroll-in before stomping.
 */
export const redListCategoriesFootprints = {
  /** Wall-clock delay after the trail enters view before the first foot pops. */
  startDelaySeconds: 3,
} as const;

/**
 * Pressure header — top-right trail first, then lower-left after it finishes
 * plus one more stagger gap. Both share one scroll-ready marker so their
 * clocks stay aligned.
 */
export const pressureFootprints = {
  /** Wall-clock delay after scroll-in before the top-right trail’s first foot. */
  startDelaySeconds: 0.5,
  /**
   * Pause after the top-right trail’s last foot before the lower-left trail’s
   * first foot (one stagger beat).
   */
  betweenTrailsDelaySeconds: birdFootprints.staggerSeconds,
} as const;

/**
 * Conclusion / “within reach” CTA — trail waits after scroll-in before stomping.
 */
export const conclusionReachFootprints = {
  /** Wall-clock delay after the trail enters view before the first foot pops. */
  startDelaySeconds: 1.5,
} as const;
